import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/shared/PasswordInput";
import { Label } from "@/components/ui/label";
import { Building2, MailCheck } from "lucide-react";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <div className="hidden md:flex md:w-1/2 bg-black relative items-end p-12"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img src="/logo-192.png" alt="Proyekty" className="w-14 h-14" />
            <div className="text-5xl font-bold tracking-tight text-white">Proyekty</div>
          </div>
          <p className="text-white text-xl font-semibold mt-5 max-w-sm text-balance">Víte, co se děje na každé stavbě.</p>
          <p className="text-white/70 mt-1 max-w-sm">Méně papíru, víc klidu.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-2.5 mb-8">
            <img src="/logo-192.png" alt="Proyekty" className="w-9 h-9" />
            <div className="min-w-0">
              <div className="text-3xl font-bold tracking-tight">Proyekty</div>
              <div className="text-[13px] text-muted-foreground leading-snug">Víte, co se děje na každé stavbě.</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          {subtitle && <p className="text-muted-foreground text-sm mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

// Přihlášení: primárně e-mailový kód (OTP), heslo jako záložní cesta.
// Kroky: email -> code -> (select firmy, když má e-mail víc členství) | password (fallback).
export default function Login() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [select, setSelect] = useState(null); // { select_token, memberships }
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);
  const { applyAuth } = useAuth();
  const codeRef = useRef();

  const run = async (fn) => {
    setError(""); setLoading(true);
    try { await fn(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const requestCode = (e) => { e?.preventDefault(); run(async () => {
    await api.post("/auth/otp/request", { email });
    setCode(""); setStep("code");
    setTimeout(() => codeRef.current?.focus(), 50);
  }); };

  const resend = () => { setResent(false); run(async () => {
    await api.post("/auth/otp/request", { email });
    setResent(true);
  }); };

  const verifyCode = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/otp/verify", { email, code });
    if (data.select_required) { setSelect(data); setStep("select"); }
    else applyAuth(data);
  }); };

  const chooseCompany = (mid) => run(async () => {
    const { data } = await api.post("/auth/otp/select", { select_token: select.select_token, membership_id: mid });
    applyAuth(data);
  });

  const loginPassword = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data);
  }); };

  return (
    <AuthShell title="Přihlášení"
      subtitle={step === "email" ? "Zadejte svůj e-mail, pošleme vám přihlašovací kód"
        : step === "code" ? null : step === "select" ? "Vyberte firmu, do které se chcete přihlásit"
        : "Přihlášení heslem (záložní možnost)"}>

      {step === "email" && (
        <form onSubmit={requestCode} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" data-testid="login-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} className="h-12 mt-1.5" required autoFocus />
          </div>
          {error && <div data-testid="login-error" className="text-destructive text-sm">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 text-base font-bold">
            {loading ? "Odesílám kód…" : "Pokračovat"}
          </Button>
          <div className="flex justify-between text-sm text-muted-foreground pt-2">
            <button type="button" onClick={() => { setError(""); setStep("password"); }} data-testid="login-use-password"
              className="hover:text-foreground">Přihlásit se heslem</button>
            <Link to="/register" data-testid="go-register" className="text-brand font-semibold hover:underline">Registrovat firmu</Link>
          </div>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={verifyCode} className="space-y-4">
          <div className="flex flex-col items-center text-center gap-2 pb-1">
            <MailCheck size={34} className="text-brand" />
            <p className="text-sm text-muted-foreground">Odeslali jsme 6místný kód na<br /><b className="text-foreground">{email}</b></p>
          </div>
          <div>
            <Label htmlFor="code">Kód z e-mailu</Label>
            <Input id="code" ref={codeRef} data-testid="login-code" inputMode="numeric" autoComplete="one-time-code"
              pattern="[0-9]*" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="h-12 mt-1.5 text-center text-2xl tracking-[0.5em] font-bold" required />
          </div>
          {error && <div data-testid="login-error" className="text-destructive text-sm">{error}</div>}
          {resent && <div className="text-sm text-emerald-600 dark:text-emerald-400">Kód znovu odeslán.</div>}
          <Button type="submit" disabled={loading || code.length < 6} data-testid="login-code-submit" className="w-full h-12 text-base font-bold">
            {loading ? "Ověřuji…" : "Přihlásit se"}
          </Button>
          <div className="flex justify-between text-sm text-muted-foreground pt-2">
            <button type="button" onClick={() => { setError(""); setStep("email"); }} className="hover:text-foreground">Změnit e-mail</button>
            <button type="button" onClick={resend} data-testid="login-resend" className="text-brand font-semibold hover:underline">Poslat kód znovu</button>
          </div>
        </form>
      )}

      {step === "select" && select && (
        <div className="space-y-2" data-testid="company-select">
          {select.memberships.map((m, i) => (
            <button key={m.membership_id} onClick={() => chooseCompany(m.membership_id)} disabled={loading}
              data-testid={`company-option-${i}`}
              className="w-full flex items-center gap-3 rounded-[14px] border border-border p-4 text-left hover:border-brand transition-colors">
              <Building2 size={22} className="text-brand shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-bold truncate">{m.company_name}</div>
                <div className="text-xs text-muted-foreground">{m.role_label}</div>
              </div>
              {m.unread > 0 && (
                <span data-testid={`company-option-unread-${i}`} className="shrink-0 min-w-[22px] h-[22px] px-1.5 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center tabular-nums">
                  {m.unread > 99 ? "99+" : m.unread}
                </span>
              )}
            </button>
          ))}
          {error && <div data-testid="login-error" className="text-destructive text-sm pt-1">{error}</div>}
          <button type="button" onClick={() => { setError(""); setStep("email"); }}
            className="text-sm text-muted-foreground hover:text-foreground pt-2">Zpět na přihlášení</button>
        </div>
      )}

      {step === "password" && (
        <form onSubmit={loginPassword} className="space-y-4">
          <div>
            <Label htmlFor="email-pw">E-mail</Label>
            <Input id="email-pw" data-testid="login-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} className="h-12 mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="password">Heslo</Label>
            <PasswordInput id="password" data-testid="login-password" value={password}
              onChange={(e) => setPassword(e.target.value)} className="h-12 mt-1.5" required />
          </div>
          {error && <div data-testid="login-error" className="text-destructive text-sm">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 text-base font-bold">
            {loading ? "Přihlašuji…" : "Přihlásit se"}
          </Button>
          <div className="flex justify-between text-sm text-muted-foreground pt-2">
            <button type="button" onClick={() => { setError(""); setStep("email"); }} data-testid="login-use-otp"
              className="text-brand font-semibold hover:underline">Přihlásit se kódem</button>
            <Link to="/forgot-password" className="hover:text-foreground">Zapomenuté heslo?</Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
